import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface CommandPaletteContextValue {
  isOpen: boolean;
  query: string;
  setQuery: (query: string) => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
}

export const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPaletteContext(): CommandPaletteContextValue {
  const value = useContext(CommandPaletteContext);

  if (!value) {
    throw new Error('useCommandPaletteContext must be used within CommandPaletteProvider');
  }

  return value;
}

interface CommandPaletteProviderProps {
  children: ReactNode;
}

export function CommandPaletteProvider({ children }: CommandPaletteProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const openCommandPalette = useCallback(() => {
    setQuery('');
    setIsOpen(true);
  }, []);

  const closeCommandPalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  const toggleCommandPalette = useCallback(() => {
    setIsOpen((current) => {
      if (current) {
        setQuery('');
        return false;
      }

      setQuery('');
      return true;
    });
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({
      isOpen,
      query,
      setQuery,
      openCommandPalette,
      closeCommandPalette,
      toggleCommandPalette,
    }),
    [closeCommandPalette, isOpen, openCommandPalette, query, toggleCommandPalette],
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}
